"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { SessionIdUtil } from "@amerisave/amc-client-beacon";
import { getTrackingInfo } from "@/components/amc-pixel/amc-pixel-utils";
import { createUserSession, insertUserProgress, ITrackUserProgressRequest } from "@/services/backend-service";
import { OptimizelyUser } from "@/lib/optimizely";
interface IUserTrackingProps {
  optimizelyUser?: OptimizelyUser;
}
const useTracking = (props?: IUserTrackingProps): void => {
  const {
    optimizelyUser
  } = props || {};
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastVisitedRef = useRef<string | null>(null);
  const lastStartTimeRef = useRef<string | null>(null);
  const hasTrackedCurrentPageRef = useRef<boolean>(false);
  const optimizelySessionId = optimizelyUser?.attributes.sessionId.toString();
  useEffect(() => {
    let isUnmounting = false;
    const trackPageVisit = async () => {
      try {
        const trackingInfo = await getTrackingInfo();
        const {
          sourceInfoId,
          customerId
        } = trackingInfo;
        const visitorId = getVisitorIdFromCookie();
        let sessionId = sessionStorage.getItem("sessionId");

        // Create session if it doesn't exist
        if (!sessionId) {
          sessionId = new SessionIdUtil().get();

          // Validate sessionId before storing
          if (!sessionId || sessionId.trim() === "") {
            console.error("Failed to generate valid sessionId");
            return;
          }
          sessionStorage.setItem("sessionId", sessionId);
          await createUserSession({
            sessionId,
            userAgent: navigator.userAgent,
            sourceInfoId,
            visitorId,
            customerId: Number(customerId) === 0 ? null : customerId,
            optimizelySessionId
          });
        }
        const currentUrl = getVisitedUrl(window.location.href);

        // Validate required data
        if (!sessionId || sessionId.trim() === "") {
          return;
        }
        if (!currentUrl || currentUrl.trim() === "") {
          return;
        }

        // Skip if we're on the same page (refresh scenario)
        if (lastVisitedRef.current === currentUrl && hasTrackedCurrentPageRef.current) {
          return;
        }
        const now = new Date().toISOString();

        // If user is leaving previous page, mark its completion
        if (lastVisitedRef.current && lastVisitedRef.current !== currentUrl && !isUnmounting) {
          await insertUserProgress({
            sessionId,
            urlVisited: lastVisitedRef.current,
            completed: now,
            optimizelySessionId
          });
        }

        // Log new visit (insert start)
        await insertUserProgress({
          sessionId,
          urlVisited: currentUrl,
          start: now,
          sourceInfoId,
          optimizelySessionId
        });

        // Save references for next navigation
        lastVisitedRef.current = currentUrl;
        lastStartTimeRef.current = now;
        hasTrackedCurrentPageRef.current = true;
      } catch (error) {
        console.error("Error in useTracking:", error);
      }
    };

    // Run when route changes
    trackPageVisit();

    // When component unmounts or route changes — update dComplete
    return () => {
      isUnmounting = true;
      const sessionId_0 = sessionStorage.getItem("sessionId");
      const urlVisited = lastVisitedRef.current;

      // Validate data before sending beacon
      if (sessionId_0 && sessionId_0.trim() !== "" && urlVisited && urlVisited.trim() !== "") {
        const completeData: ITrackUserProgressRequest = {
          sessionId: sessionId_0,
          urlVisited,
          completed: new Date().toISOString(),
          optimizelySessionId
        };
        navigator.sendBeacon(`${process.env.NEXT_PUBLIC_API_BASE_URL}/tracking/progress`, JSON.stringify(completeData));
      }

      // Reset tracking flag when leaving the page
      hasTrackedCurrentPageRef.current = false;
    };
  }, [pathname, searchParams]);
};

// Helper: Extract visitorId from cookies
function getVisitorIdFromCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find(row => row.startsWith("VISITORID="));
  return match ? match.split("=")[1] : null;
}

// Helper: Get simplified path
function getVisitedUrl(url: string): string {
  const mainDomain = window.location.hostname;
  const parsedUrl = new URL(url);
  if (parsedUrl.hostname === mainDomain) {
    return parsedUrl.pathname === "/" ? "home" : parsedUrl.pathname;
  }
  return parsedUrl.pathname;
}
export default useTracking;